/**
 * Sprint V98 · Webinar-Bestaetigungsseite (Stage 1).
 * Route: /webinar/:id/bestaetigung?email=<email>
 */
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { CalendarCheck, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import PostSignupInboxHint from "@/components/webinar/PostSignupInboxHint";

function fmtDateDe(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const fmt = new Intl.DateTimeFormat("de-DE", {
      timeZone: "Europe/Berlin",
      weekday: "short",
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
    const p = fmt.formatToParts(d).reduce((a, x) => ({ ...a, [x.type]: x.value }), {});
    return `${p.weekday}, ${p.day}.${p.month}.${p.year} · ${p.hour}:${p.minute} Uhr`;
  } catch {
    return iso;
  }
}

function buildIcsDownload(config) {
  if (!config?.event_datetime) return null;
  const dtStart = new Date(config.event_datetime);
  const dtEnd = new Date(dtStart.getTime() + (config.duration_minutes || 90) * 60000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//Flex4Success//Webinar//DE",
    "BEGIN:VEVENT",
    `UID:webinar-${config.webinar_id}@flex4success`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(dtStart)}`,
    `DTEND:${fmt(dtEnd)}`,
    `SUMMARY:${config.title}`,
    `DESCRIPTION:${(config.subtitle || "").replace(/,/g, "\\,")}`,
    `URL:${config.join_url}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  return `data:text/calendar;charset=utf-8,${encodeURIComponent(ics)}`;
}

export default function WebinarConfirmed() {
  const { id = "1" } = useParams();
  const [params] = useSearchParams();
  const email = params.get("email") || "";
  const [config, setConfig] = useState(null);

  useEffect(() => {
    api.get(`/webinar/${id}`).then((r) => setConfig(r.data)).catch(() => {});
  }, [id]);

  const dateDe = useMemo(() => (config ? fmtDateDe(config.event_datetime) : ""), [config]);
  const icsUrl = useMemo(() => buildIcsDownload(config), [config]);

  return (
    <div
      className="min-h-screen bg-gradient-to-br from-slate-50 to-white dark:from-slate-950 dark:to-slate-900 flex items-center justify-center px-4 py-12"
      data-testid="webinar-confirmed"
    >
      <div className="max-w-lg w-full text-center">
        <div className="mx-auto w-20 h-20 rounded-full bg-emerald-100 dark:bg-emerald-900/40 flex items-center justify-center mb-6">
          <Sparkles className="w-10 h-10 text-emerald-600 dark:text-emerald-300" />
        </div>
        <h1
          className="text-3xl sm:text-4xl font-extrabold text-slate-900 dark:text-white"
          data-testid="webinar-confirmed-headline"
        >
          Du bist dabei.
        </h1>
        {config && (
          <div className="mt-6 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-6 text-left shadow-sm">
            <p className="text-xs uppercase tracking-widest text-[#F3651D] font-bold">
              Termin
            </p>
            <p
              className="mt-2 text-lg font-bold text-slate-900 dark:text-white"
              data-testid="webinar-confirmed-title"
            >
              {config.title}
            </p>
            <p
              className="mt-1 text-sm text-slate-600 dark:text-slate-300"
              data-testid="webinar-confirmed-date"
            >
              {dateDe}
            </p>
            {icsUrl && (
              <a
                href={icsUrl}
                download={`webinar-${config.webinar_id}.ics`}
                className="mt-5 inline-flex items-center gap-2 rounded-lg bg-[#4563BB] hover:bg-[#3a54a3] text-white px-4 py-2 text-sm font-semibold transition-colors"
                data-testid="webinar-confirmed-ics"
              >
                <CalendarCheck className="w-4 h-4" />
                Termin in Kalender speichern
              </a>
            )}
          </div>
        )}
        {/* Sprint V101.f (20.09.2026) — Provider-adaptiver Inbox-Hinweis
            statt generischem „Prüf mal den Spam-Ordner". Erhöht Confirm-Rate
            besonders bei Gmail/Outlook. */}
        <div className="mt-8 text-left">
          <PostSignupInboxHint email={email} />
        </div>
      </div>
    </div>
  );
}
